import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";
import { cloneDeep, get, isEmpty, omit } from "lodash";
import { diff } from "deep-object-diff";

export const KNOWLEDGE_BASE_FIELDS = {
  // Title and content
  title: "",
  content: "", // Rich text / markdown content
  summary: "", // Short summary for list views

  // Categorization
  category: "", // product_type, issue_type, general
  productType: "", // windows, doors, hardware, screens, etc.
  issueTypes: [], // Array of issue types this applies to (broken_glass, hardware, etc.)
  tags: [], // Additional searchable tags

  // Media attachments
  photos: [], // Array of { id, src } images (see ImagesInput)
  videoUrl: "", // External video URL (YouTube, Vimeo, etc.)
  videoEmbed: "", // Embed code for video

  // Visibility
  isPublished: true,
  isInternal: false, // If true, only visible to staff (not customers)

  // Ordering
  sortOrder: 0,
  isPinned: false, // Show at top of category

  // Stats
  viewCount: 0,
  helpfulCount: 0,
  notHelpfulCount: 0,

  // Meta
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(KNOWLEDGE_BASE_FIELDS);

// Product type categories for organizing knowledge base entries
export const PRODUCT_CATEGORIES = {
  windows: {
    label: "Windows",
    icon: "window",
    subcategories: [
      "Casement",
      "Double Hung",
      "Awning",
      "Slider",
      "Picture",
      "Bay/Bow",
      "Specialty",
    ],
  },
  doors: {
    label: "Doors",
    icon: "door",
    subcategories: [
      "Entry",
      "Patio/Sliding",
      "French",
      "Bi-Fold",
      "Storm",
      "Screen",
    ],
  },
  hardware: {
    label: "Hardware",
    icon: "hardware",
    subcategories: [
      "Locks",
      "Handles",
      "Hinges",
      "Operators",
      "Weatherstripping",
      "Screens",
    ],
  },
  glass: {
    label: "Glass",
    icon: "glass",
    subcategories: ["Insulated", "Tempered", "Laminated", "Low-E", "Grids"],
  },
  installation: {
    label: "Installation",
    icon: "installation",
    subcategories: ["Prep", "Flashing", "Shimming", "Insulation", "Trim"],
  },
  general: {
    label: "General",
    icon: "inventory",
    subcategories: ["Maintenance", "Cleaning", "Troubleshooting", "Safety"],
  },
};
deepFreeze(PRODUCT_CATEGORIES);

// Knowledge base entry categories
export const KB_CATEGORIES = {
  tips_tricks: {
    label: "Tips & Tricks",
    icon: "tips_tricks",
    description: "Quick tips for common issues",
  },
  troubleshooting: {
    label: "Troubleshooting",
    icon: "warning",
    description: "Step-by-step troubleshooting guides",
  },
  how_to: {
    label: "How-To Guide",
    icon: "description",
    description: "Detailed how-to instructions",
  },
  product_info: {
    label: "Product Information",
    icon: "info",
    description: "Product specifications and details",
  },
  safety: {
    label: "Safety",
    icon: "safety",
    description: "Safety warnings and precautions",
  },
};
deepFreeze(KB_CATEGORIES);

export default class KnowledgeBase extends DataStructure {
  constructor(data, id) {
    super(sanitize(data, KNOWLEDGE_BASE_FIELDS));
    this.id = id || null;
  }

  toString() {
    return this.data.title || "Untitled Entry";
  }

  get categoryType() {
    return KB_CATEGORIES[this.data.category] || KB_CATEGORIES.tips_tricks;
  }

  get productCategoryType() {
    return (
      PRODUCT_CATEGORIES[this.data.productType] || PRODUCT_CATEGORIES.general
    );
  }

  get hasMedia() {
    return this.data.photos?.length > 0 || !!this.data.videoUrl;
  }

  get photoCount() {
    return this.data.photos?.length || 0;
  }

  get hasVideo() {
    return !!this.data.videoUrl || !!this.data.videoEmbed;
  }

  get createdDate() {
    return timestampToDate(this.data.created);
  }

  get modifiedDate() {
    return timestampToDate(this.data.modified);
  }

  get isNew() {
    return !this.id;
  }

  // `category` is omitted because the edit dialog pre-selects a default one,
  // which would otherwise make an untouched new entry look modified.
  get isEmpty() {
    return Boolean(
      !this.id &&
        isEmpty(
          diff(
            omit(this.data, "category"),
            omit(KNOWLEDGE_BASE_FIELDS, "category")
          )
        )
    );
  }

  // Firestore converter. The store passes this class straight to
  // `withConverter()` (GET_DOC / LISTEN_DOC / LISTEN_COLLECTION), which calls
  // these two — without them every read throws
  // "this._converter.fromFirestore is not a function".
  // NOTE: this class's constructor is (data, id), not (id, data).
  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new KnowledgeBase(data, snapshot.id);
  }

  // Check if this entry applies to a specific issue type
  appliesToIssueType(issueType) {
    if (!this.data.issueTypes?.length) return true; // No filter = applies to all
    return this.data.issueTypes.includes(issueType);
  }

  // Check if this entry applies to a specific product type
  appliesToProductType(productType) {
    if (!this.data.productType) return true; // No filter = applies to all
    return this.data.productType === productType;
  }

  // Get matching score for search/filtering
  getRelevanceScore(issueType, productType, searchQuery) {
    let score = 0;

    // Exact issue type match
    if (issueType && this.data.issueTypes?.includes(issueType)) {
      score += 10;
    }

    // Exact product type match
    if (productType && this.data.productType === productType) {
      score += 10;
    }

    // Pinned entries get boost
    if (this.data.isPinned) {
      score += 5;
    }

    // Search query matching
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const title = (this.data.title || "").toLowerCase();
      const content = (this.data.content || "").toLowerCase();
      const summary = (this.data.summary || "").toLowerCase();
      const tags = (this.data.tags || []).join(" ").toLowerCase();

      if (title.includes(query)) score += 8;
      if (summary.includes(query)) score += 5;
      if (tags.includes(query)) score += 4;
      if (content.includes(query)) score += 2;
    }

    // Helpful rating boost
    const helpfulRatio =
      this.data.helpfulCount /
      (this.data.helpfulCount + this.data.notHelpfulCount || 1);
    score += helpfulRatio * 3;

    return score;
  }

  // Permissions
  permissions = {
    view: (user) => {
      if (!this.data.isPublished && !user.isEmployee) return false;
      if (this.data.isInternal && !user.isEmployee) return false;
      return true;
    },
    edit: (user) => {
      return user.isEmployee && (user.isAdmin || user.hasRole("service"));
    },
    delete: (user) => {
      return user.isAdmin;
    },
  };

  // Static method to get entries for a specific issue type
  static getEntriesForIssueType(issueType, productType = null) {
    const entries = Object.values(store.state.main.knowledgeBase || {});
    return entries
      .filter((entry) => {
        const kb = new KnowledgeBase(entry.data, entry.id);
        if (!kb.data.isPublished) return false;
        if (!kb.appliesToIssueType(issueType)) return false;
        if (productType && !kb.appliesToProductType(productType)) return false;
        return true;
      })
      .sort((a, b) => {
        // Pinned first, then by sort order
        if (a.data.isPinned !== b.data.isPinned) {
          return a.data.isPinned ? -1 : 1;
        }
        return (a.data.sortOrder || 0) - (b.data.sortOrder || 0);
      });
  }

  // Static method to search knowledge base
  static search(query, filters = {}) {
    const entries = Object.values(store.state.main.knowledgeBase || {});
    const { issueType, productType, category, includeInternal } = filters;

    return entries
      .filter((entry) => {
        if (!entry.data.isPublished) return false;
        if (entry.data.isInternal && !includeInternal) return false;
        if (category && entry.data.category !== category) return false;
        if (issueType && !entry.data.issueTypes?.includes(issueType))
          return false;
        if (productType && entry.data.productType !== productType) return false;
        return true;
      })
      .map((entry) => {
        const kb = new KnowledgeBase(entry.data, entry.id);
        return {
          ...entry,
          relevanceScore: kb.getRelevanceScore(issueType, productType, query),
        };
      })
      .filter((entry) => !query || entry.relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
  }
}
